import React, { FC, useState, useRef } from 'react';
import { Spin } from 'antd';
import { LeftOutlined, PlusOutlined, CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import { uploadSupportScreenshot } from 'src/api/services/firebaseStorage';
import { useSubmitSupportReportMutation, useBlockClientMutation } from 'src/api/supportReports';
import toast from 'react-hot-toast';
import { ReactComponent as ReportIssueInappropriateIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/report-issue/report-issue-inappropriate-behaviour.svg';
import { ReactComponent as ReportIssueHarassmentIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/report-issue/report-issue-harassment-or-abuse.svg';
import { ReactComponent as ReportIssueSpamIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/report-issue/report-issue-spam-or-unwanted-messages.svg';
import { ReactComponent as ReportIssuePaymentIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/report-issue/report-issue-payment-or-billing.svg';
import { ReactComponent as ReportIssueTechnicalIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/report-issue/report-issue-technical-issue.svg';
import { ReactComponent as ReportIssueOtherIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/report-issue/report-issue-other.svg';
import { ReactComponent as ReportIssueCheckmarkIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/report-issue/report-issue-checkmark.svg';
import { ReactComponent as ReportSubmittedSentIcon } from 'src/assets/images/onboarding-trainer/dashboard/checkins/checkin-request-sent.svg';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ReactComponent as ReportBackArrowIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/report-issue/report-back-arrow.svg';
import './ReportIssueView.scss';

interface ReportIssueViewProps {
  chatId: string;
  trainerUid: string;
  clientUid: string;
  onClose: () => void;
  onBlocked?: () => void;
}

const CATEGORIES = [
  { name: 'Inappropriate behaviour', icon: <ReportIssueInappropriateIcon /> },
  { name: 'Harassment or abuse', icon: <ReportIssueHarassmentIcon /> },
  { name: 'Spam or unwanted messages', icon: <ReportIssueSpamIcon /> },
  { name: 'Payment or billing', icon: <ReportIssuePaymentIcon /> },
  { name: 'Technical issue', icon: <ReportIssueTechnicalIcon /> },
  { name: 'Other', icon: <ReportIssueOtherIcon /> },
];

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
const MAX_SIZE_MB = 5;

export const ReportIssueView: FC<ReportIssueViewProps> = ({
  chatId,
  trainerUid,
  clientUid,
  onClose,
  onBlocked,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [details, setDetails] = useState('');
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const submitReportMutation = useSubmitSupportReportMutation();
  const blockClientMutation = useBlockClientMutation();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;

    const newFiles = Array.from(e.target.files);
    const validFiles: File[] = [];

    newFiles.forEach((file) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name} is not a supported image format.`);
        return;
      }
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        toast.error(`${file.name} exceeds the 5MB size limit.`);
        return;
      }
      validFiles.push(file);
    });

    if (screenshots.length + validFiles.length > 5) {
      toast.error('You can only attach up to 5 screenshots.');
      validFiles.splice(5 - screenshots.length);
    }

    setScreenshots((prev) => [...prev, ...validFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeScreenshot = (index: number) => {
    setScreenshots((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!selectedCategory) {
      toast.error('Please select an issue category.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Parallel upload of screenshots
      const uploadPromises = screenshots.map((file) =>
        uploadSupportScreenshot(file, trainerUid).catch((e) => {
          console.error('Screenshot upload failed', e);
          throw new Error(`Failed to upload ${file.name}`);
        }),
      );

      const uploadedUrls = await Promise.all(uploadPromises);

      // Submit report via backend Cloud Function API
      await submitReportMutation.mutateAsync({
        chatId,
        reportedUid: clientUid,
        category: selectedCategory,
        details: details.trim(),
        screenshots: uploadedUrls,
      });

      setIsSubmitted(true);
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBlockClient = async () => {
    try {
      await blockClientMutation.mutateAsync({
        clientUid,
        chatId,
        reason: selectedCategory || 'Reported issue block',
      });
      toast.success('Client blocked successfully', { id: 'block-client-success' });
      queryClient.invalidateQueries({ queryKey: ['trainer-clients'] });
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      queryClient.invalidateQueries({ queryKey: ['chats'] });
      queryClient.invalidateQueries({ queryKey: ['checkins'] });
      queryClient.invalidateQueries({ queryKey: ['trainer-messages-page'] });
      queryClient.invalidateQueries({ queryKey: ['trainer-program-plan-list'] });
      onBlocked?.();
      onClose();
      navigate('/onboarding-trainer/dashboard/messages', { replace: true });
    } catch (error: any) {
      toast.error(error.message || 'Failed to block client. Please try again.');
    }
  };

  if (isSubmitted) {
    return (
      <div className="report-issue-view submitted">
        <div className="success-content">
          <div className="success-icon-badge">
            <ReportSubmittedSentIcon />
          </div>
          <h2>Report submitted!</h2>
          <p>
            Thanks for reporting this issue. Our support team will review the details and any
            screenshots you attached.
          </p>
          <div className="success-actions">
            <button
              className={`block-button ${blockClientMutation.isPending ? 'submitting' : ''}`}
              onClick={handleBlockClient}
              disabled={blockClientMutation.isPending}
            >
              {blockClientMutation.isPending ? (
                <Spin
                  indicator={<LoadingOutlined style={{ color: '#04051b', fontSize: 20 }} spin />}
                />
              ) : (
                'Block client'
              )}
            </button>
            <button
              className="back-button"
              onClick={onClose}
              disabled={blockClientMutation.isPending}
            >
              <ReportBackArrowIcon className="back-arrow-icon" /> Back to chat
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="report-issue-view">
      <div className="report-header">
        <button className="back-nav" onClick={onClose}>
          <LeftOutlined />
        </button>
        <h2>Report an issue</h2>
      </div>

      <div className="report-content-scroll">
        <div className="report-content">
          <div className="section-title">What&apos;s the issue about?</div>
          <div className="category-list">
            {CATEGORIES.map((category) => (
              <button
                key={category.name}
                type="button"
                className={`category-item ${selectedCategory === category.name ? 'selected' : ''}`}
                onClick={() => setSelectedCategory(category.name)}
              >
                <div className="category-content">
                  <span className="category-icon">{category.icon}</span>
                  <span className="category-text">{category.name}</span>
                </div>
                {selectedCategory === category.name && (
                  <div className="selected-check-badge">
                    <ReportIssueCheckmarkIcon />
                  </div>
                )}
              </button>
            ))}
          </div>

          <div className="section-title">Please provide more details</div>
          <div className="details-container">
            <textarea
              placeholder="Please describe the issue..."
              value={details}
              onChange={(e) => setDetails(e.target.value.substring(0, 500))}
              maxLength={500}
              rows={5}
            />
            <div className="char-count">{details.length}/500</div>
          </div>

          <div className="section-title">
            Add screenshots <span>(optional)</span>
          </div>
          <div className="screenshots-subtitle">You can add up to 5 screenshots</div>
          <div className="screenshots-container">
            {screenshots.length < 5 && (
              <div className="upload-box" onClick={() => fileInputRef.current?.click()}>
                <PlusOutlined />
              </div>
            )}
            {screenshots.map((file, idx) => (
              <div key={idx} className="screenshot-preview">
                <img src={URL.createObjectURL(file)} alt={`Screenshot ${idx + 1}`} />
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeScreenshot(idx)}
                  aria-label="Remove screenshot"
                >
                  <CloseOutlined />
                </button>
              </div>
            ))}
          </div>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
            multiple
            onChange={handleFileChange}
          />

          {(() => {
            const isSubmittingReport = isSubmitting || submitReportMutation.isPending;
            return (
              <button
                className={`submit-report-btn ${isSubmittingReport ? 'submitting' : ''}`}
                onClick={handleSubmit}
                disabled={!selectedCategory || isSubmittingReport}
              >
                {isSubmittingReport ? (
                  <Spin
                    indicator={<LoadingOutlined style={{ color: '#04051b', fontSize: 22 }} spin />}
                  />
                ) : (
                  'Submit Report'
                )}
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  );
};
